import { useState } from "react";
import { useNavigate } from "@tanstack/react-router";
import { useEligibility, useStartRun, useSubmitRun } from "@/app/gradingQueries";

const message = (e: unknown, fallback: string) => (e instanceof Error && e.message ? e.message : fallback);

/** «проверить на сервере» (S5, S6): перед прогоном — сохранить черновик
 *  (сервер снимает версию с сохранённого), затем прогон и переход в S6. */
export function useServerCheck(assignmentId: string, beforeStart?: () => Promise<boolean>) {
  const start = useStartRun(assignmentId);
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const run = async () => {
    setError(null);
    setBusy(true);
    try {
      if (beforeStart && !(await beforeStart())) {
        setError("Черновик не сохранился — сервер проверил бы старую версию. Проверь связь и нажми ещё раз.");
        return;
      }
      const r = await start.mutateAsync();
      void navigate({ to: "/notebook/$assignmentId/check", params: { assignmentId }, search: { run: r.id } });
    } catch (e) {
      setError(message(e, "Прогон не запустился. Черновик сохранён — попробуй ещё раз."));
    } finally {
      setBusy(false);
    }
  };
  return { run, busy: busy || start.isPending, error };
}

/** «отправить на проверку» (S5, S6): можно ли и почему нет — только из
 *  eligibility сервера (правило 3); отказ сервера показывается как причина. */
export function useSubmitFlow(assignmentId: string) {
  const elig = useEligibility(assignmentId);
  const submit = useSubmitRun(assignmentId);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const data = elig.data;
  const submitted = sent || !!data?.submitted;

  const reason = elig.error
    ? "Не удалось получить условия отправки — проверь связь и обнови страницу."
    : !data
      ? "Проверяем условия отправки…"
      : data.reason;

  const send = async () => {
    if (!data?.eligible) return;
    setError(null);
    try {
      await submit.mutateAsync(data.run_id ?? undefined);
      setSent(true);
    } catch (e) {
      setError(message(e, "Не удалось отправить. Прогон сохранён — попробуй ещё раз."));
    }
  };

  return { elig: data, submitted, eligible: !!data?.eligible && !submitted, reason, send, sending: submit.isPending, error };
}
